import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, Download, FileText, X, FileArchive } from "lucide-react";
import API_BASE_URL from "../api";

// ⚠️ Adjust to match your real backend route once it exists.
const API_BASE = `${API_BASE_URL}/api/downloads`;

const TYPE_OPTIONS = ["All Types", "Certificate", "Report", "Export"];
const PAGE_SIZE = 10;

function TypeBadge({ type }) {
  const colorMap = {
    Certificate: "bg-emerald-50 text-emerald-700",
    Report: "bg-blue-50 text-blue-700",
    Export: "bg-purple-50 text-purple-700",
  };
  return (
    <span className={`text-xs font-medium px-2 py-1 rounded-full ${colorMap[type] || "bg-gray-50 text-gray-600"}`}>
      {type || "File"}
    </span>
  );
}

export default function AdminDownloads() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      setError(null);
      try {
        // ⚠️ Placeholder endpoint — replace with your real "list downloadable files" route.
        const res = await fetch(`${API_BASE}/allFiles`).then((r) => r.json());
        setFiles(res?.files || []);
      } catch (err) {
        console.error("Failed to load downloads:", err);
        setError("Couldn't load downloads. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchFiles();
  }, []);

  const filtered = useMemo(() => {
    return files.filter((f) => {
      const matchesSearch = !search || f.name?.toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === "All Types" || f.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [files, search, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter]);

  const handleBulkDownload = () => {
    paginated.forEach((f) => {
      if (f.fileUrl) window.open(f.fileUrl, "_blank");
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Download Center</h3>
          <p className="text-sm text-gray-500">
            {loading ? "Loading…" : `${filtered.length} file${filtered.length !== 1 ? "s" : ""} available`}
          </p>
        </div>
        <button
          onClick={handleBulkDownload}
          disabled={paginated.length === 0}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors"
        >
          <FileArchive size={16} />
          Download All (this page)
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by file name…"
            className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="relative">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 text-sm rounded-xl border border-gray-200 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {error && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs">
                <th className="px-6 py-3 font-medium">File Name</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Size</th>
                <th className="px-4 py-3 font-medium">Generated On</th>
                <th className="px-6 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                    Loading files…
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <FileText size={24} className="text-gray-300" />
                      No files match your filters
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((row, i) => (
                  <tr
                    key={row._id || row.name || i}
                    className={i !== paginated.length - 1 ? "border-b border-gray-50" : ""}
                  >
                    <td className="px-6 py-4 font-medium text-gray-800 flex items-center gap-2">
                      <FileText size={15} className="text-gray-400" />
                      {row.name || "—"}
                    </td>
                    <td className="px-4 py-4">
                      <TypeBadge type={row.type} />
                    </td>
                    <td className="px-4 py-4 text-gray-600">{row.size || "—"}</td>
                    <td className="px-4 py-4 text-gray-500">
                      {row.generatedOn ? new Date(row.generatedOn).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {row.fileUrl && (
                        <a href={row.fileUrl} download title="Download file">
                          <Download size={16} className="cursor-pointer text-gray-400 hover:text-gray-600 inline-block" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-50 text-sm text-gray-500">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Prev
              </button>
              <span className="text-gray-400 text-xs">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}